import React, { useEffect, useRef, useState, useCallback } from "react";
import { TabInterface } from "../../types/dashboard.type";
import { PersonInterface } from "../../types/post.type";

interface InfoMovieProps {
  releaseYear: TabInterface[];
  categories: TabInterface[];
  directors: PersonInterface[];
  actors: PersonInterface[];
  description: string;
  descExpanded: boolean;
  setDescExpanded: (v: boolean) => void;
}

/**
 * Helper: render list of tags with trailing commas (except after the last item)
 */
const renderTags = (arr: TabInterface[] = []) =>
  arr?.map((item, index) => (
    <a
      key={item.id || item.slug}
      href={item.slug ? `/vi/movie/category/${item.slug}/` : "#"}
      className="inline-block text-white hover:underline mr-2 mb-2"
    >
      {item.name}
      {index < arr.length - 1 ? "," : ""}
    </a>
  ));

/**
 * Helper: render list of people (directors, actors)
 */
const renderPersons = (arr: PersonInterface[] = []) =>
  arr?.map((person) => (
    <a
      key={person.id}
      href={person.slug ? `/vi/actor/${person.slug}/` : "#"}
      className="inline-block text-white hover:underline mr-2 mb-2"
    >
      {person.title}
    </a>
  ));

/**
 * This component shows general information of a movie and handles the
 * "Xem thêm / Thu gọn" toggle for the description. The toggle only appears
 * if the text truly overflows the 3-line clamp, **not** based on a fixed
 * character count.
 */
const InfoMovie: React.FC<InfoMovieProps> = ({
  releaseYear,
  categories,
  directors,
  actors,
  description,
  descExpanded,
  setDescExpanded,
}) => {
  /**
   * descRef points to the <p> element that holds the description text. We use
   * its scrollHeight vs. clientHeight to know if the text overflows the 3-line
   * CSS clamp.
   */
  const descRef = useRef<HTMLParagraphElement>(null);
  const [toggleNeeded, setToggleNeeded] = useState(false);

  /**
   * Measure overflow. We temporarily apply the clamp class so the measurement
   * works no matter what state we are in.
   */
  const measureOverflow = useCallback(() => {
    const el = descRef.current;
    if (!el) return;

    // Force the element into the clamped state to measure correctly
    const wasExpanded = !el.classList.contains("line-clamp-3");
    if (wasExpanded) {
      el.classList.add("line-clamp-3", "overflow-hidden");
    }

    const clampHeight = el.clientHeight; // height with clamp

    // Remove clamp to measure full height
    el.classList.remove("line-clamp-3", "overflow-hidden");
    const fullHeight = el.scrollHeight; // full height without clamp

    // Restore original state
    if (wasExpanded) {
      el.classList.remove("line-clamp-3", "overflow-hidden");
    } else {
      el.classList.add("line-clamp-3", "overflow-hidden");
    }

    setToggleNeeded(fullHeight > clampHeight);
  }, []);

  // Re-measure on mount, when description changes, or when window is resized
  useEffect(() => {
    measureOverflow();
    window.addEventListener("resize", measureOverflow);
    return () => window.removeEventListener("resize", measureOverflow);
  }, [description, measureOverflow]);

  return (
    <div>
      <div className="mt-4 md:mt-8 border-t border-b border-[#292929] p-2 md:p-4 flex flex-wrap items-center">
        <div className="item w-full sm:w-1/3 mb-2 sm:mb-0">
          <h2 className="title text-sm md:text-base text-grey-light mb-1 font-semibold">Ngày ra mắt</h2>
          <h3 className="content text-xs md:text-sm text-white min-h-8 md:min-h-16">
            {releaseYear?.map((y: TabInterface) => y.name).join(", ")}
          </h3>
        </div>
        <div className="item w-full sm:w-2/3 mb-2 sm:mb-0">
          <h2 className="title text-sm md:text-base text-grey-light mb-1 font-semibold">Tags</h2>
          <h3 className="content text-xs md:text-sm text-white min-h-8 md:min-h-16">{renderTags(categories)}</h3>
        </div>
        <div className="item w-full sm:w-1/3 mb-2 sm:mb-0">
          <h2 className="title text-sm md:text-base text-grey-light mb-1 font-semibold">Đạo diễn</h2>
          <h3 className="content text-xs md:text-sm text-white min-h-8 md:min-h-16">
            {directors?.length ? renderPersons(directors) : "N/A"}
          </h3>
        </div>
        <div className="item w-full sm:w-2/3">
          <h2 className="title text-sm md:text-base text-grey-light mb-1 font-semibold">Diễn viên</h2>
          <h3 className="content text-xs md:text-sm text-white min-h-8 md:min-h-16">
            {actors?.length ? renderPersons(actors) : "N/A"}
          </h3>
        </div>
      </div>

      <div className="description w-full h-auto my-3 md:my-6 px-2 md:px-4">
        <h2 className="title text-sm md:text-base text-grey-light mb-1 font-semibold">Nội dung</h2>
        <div className="content text-white text-start">
          <p
            ref={descRef}
            className={`desc-text text-xs md:text-sm transition-all duration-300 ${
              descExpanded ? "" : "line-clamp-3 overflow-hidden"
            }`}
          >
            {description}
          </p>
        </div>

        {toggleNeeded && (
          <div className="action text-center mt-2 md:mt-4 select-none">
            <span
              className="readmore-toggle text-blue text-xs md:text-sm cursor-pointer"
              role="button"
              aria-expanded={descExpanded}
              onClick={() => setDescExpanded(!descExpanded)}
            >
              {descExpanded ? "Thu gọn" : "Xem thêm"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default InfoMovie;
